import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useQueryClient } from '@tanstack/react-query';
import {
  View,
  Text,
  FlatList,
  StyleSheet,
  ActivityIndicator,
  TouchableOpacity,
} from "react-native";
import { Stack, Link, useLocalSearchParams, usePathname, useRouter } from 'expo-router';
import { FontAwesome6 } from '@expo/vector-icons';
import { fonts, interSemiBold, interRegular } from "../../../../lib/fonts";
import { useFontLoading } from "../../../../lib/FontContext";
import Header from "../../../../components/Header";
import MetaTags from "../../../../components/MetaTags";
import colors from "../../../../lib/colors";
import Footer from "../../../../components/Footer";
import { useProjectSummariesInfinite, ProjectPaperWithSummary } from "../../../../lib/hooks/useProjectSummaries";
import { useProject } from "../../../../lib/ProjectContext";
import { withProjectProvider } from "../../../../components/withProjectProvider";
import PaperCard from "../../../../components/papers/PaperCard";
import SearchInput from "../../../../components/ui/SearchInput";
import Banner from "../../../../components/ui/Banner";
import { attachStandalonePaperBackLink, getSimplePaperUrl, getShortId } from "../../../../lib/paperUrl";
import { buildCanonicalDashboardPath, buildDashboardPath } from "../../../../lib/dashboardRoutes";
import { useCurrentEntitlements } from "../../../../lib/hooks/useCurrentEntitlements";
import {
  buildBillingPreviewTrialHref,
  classifyBillingPreviewProjectAccess,
  getBillingPreviewState,
} from "../../../../lib/billingPreviewState";
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import type { LocalizedMessage } from '../../../../lib/i18n/messages';
import { useLingui } from '@lingui/react';

function ProjectSummariesPage() {
  const flatListRef = useRef<FlatList>(null);
  const { digestId } = useLocalSearchParams<{ digestId: string }>();
  const pathname = usePathname();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { fontsLoaded } = useFontLoading();
  const { i18n } = useLingui();
  const { project, isLoading: isProjectLoading } = useProject();
  const [searchQuery, setSearchQuery] = useState("");
  const [previewRefreshError, setPreviewRefreshError] = useState<LocalizedMessage | null>(null);
  const [verifiedPausedAccessKey, setVerifiedPausedAccessKey] = useState<string | null>(null);
  const previewPauseRefreshRef = useRef<string | null>(null);
  const entitlementsQuery = useCurrentEntitlements();
  const serverPreviewAccess = classifyBillingPreviewProjectAccess(
    entitlementsQuery.data,
    digestId,
    {
      isChecking: entitlementsQuery.isFetching,
      isError: entitlementsQuery.isError,
    }
  );
  const pausedAt = (project as any)?.paused_at as string | null | undefined;
  const pausedAccessKey = digestId && pausedAt ? `${digestId}:${pausedAt}` : null;
  const pausedAccessNeedsVerification = Boolean(
    pausedAccessKey && verifiedPausedAccessKey !== pausedAccessKey
  );
  const previewAccess = serverPreviewAccess === 'restricted'
    ? 'restricted'
    : pausedAccessNeedsVerification
      ? previewRefreshError
        ? 'unavailable'
        : 'checking'
      : serverPreviewAccess;
  const previewAccessRetryMessage = previewRefreshError
    ?? (previewAccess === 'unavailable' || (previewAccess === 'restricted' && entitlementsQuery.isError)
      ? msg({
        id: 'projectSummaries.accessVerificationFailed',
        message: "Paperzilla could not verify this project's access. Summaries remain unavailable until you retry.",
      })
      : null);
  const billingPreviewState = getBillingPreviewState(entitlementsQuery.data);
  const previewTrialHref = buildBillingPreviewTrialHref({
    projectId: billingPreviewState.projectId,
    resultDigestId: billingPreviewState.resultDigestId,
  });

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProjectSummariesInfinite(digestId, {
    enabled: !isProjectLoading && Boolean(project) && previewAccess === 'unrestricted',
  });

  const refreshPreviewAccess = useCallback(async () => {
    setPreviewRefreshError(null);
    try {
      await entitlementsQuery.refreshAuthoritatively();
      if (pausedAccessKey) {
        setVerifiedPausedAccessKey(pausedAccessKey);
      }
    } catch (refreshError) {
      console.error('[ProjectSummariesPage] Failed to refresh access:', refreshError);
      setPreviewRefreshError(
        msg({
          id: 'projectSummaries.accessRefreshFailed',
          message: "Paperzilla could not refresh this project's access. Summaries remain unavailable until you retry.",
        })
      );
    }
  }, [entitlementsQuery.refreshAuthoritatively, pausedAccessKey]);

  useEffect(() => {
    if (!pausedAccessKey) {
      previewPauseRefreshRef.current = null;
      return;
    }

    if (previewPauseRefreshRef.current === pausedAccessKey) {
      return;
    }

    previewPauseRefreshRef.current = pausedAccessKey;
    void refreshPreviewAccess();
  }, [pausedAccessKey, refreshPreviewAccess]);

  useEffect(() => {
    if (!digestId || previewAccess === 'unrestricted') {
      return;
    }

    void queryClient.cancelQueries({ queryKey: ['projectSummaries-infinite', digestId] });
  }, [digestId, previewAccess, queryClient]);

  // Flatten pages and deduplicate
  const allPapers = useMemo(() => {
    if (!data?.pages) return [];
    const seenIds = new Set<string>();
    const result: ProjectPaperWithSummary[] = [];
    for (const page of data.pages) {
      for (const paper of page.papers) {
        if (seenIds.has(paper.paper.id)) continue;
        seenIds.add(paper.paper.id);
        result.push(paper);
      }
    }
    return result;
  }, [data]);

  // Filter papers based on search query
  const filteredPapers = useMemo(() => {
    if (!searchQuery.trim()) return allPapers;
    const query = searchQuery.toLowerCase();
    return allPapers.filter(item => {
      const paper = item.paper;
      if (paper.title.toLowerCase().includes(query)) return true;
      if (item.paperzillaTitle.toLowerCase().includes(query)) return true;
      if (paper.authors && Array.isArray(paper.authors)) {
        if (paper.authors.some(author => author.name.toLowerCase().includes(query))) return true;
      }
      if (item.eli5.toLowerCase().includes(query)) return true;
      return false;
    });
  }, [allPapers, searchQuery]);
  const visiblePapers = previewAccess === 'unrestricted' ? filteredPapers : [];

  useEffect(() => {
    if (previewAccess !== 'unrestricted' && searchQuery) {
      setSearchQuery("");
    }
  }, [previewAccess, searchQuery]);

  const projectName = project?.name || 'Project';
  const projectPath = buildDashboardPath(pathname, `/${digestId}`);
  const summariesPath = buildDashboardPath(pathname, `/${digestId}/summaries`);

  const handleEndReached = useCallback(() => {
    if (previewAccess === 'unrestricted' && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, previewAccess]);

  const renderItem = useCallback(({ item }: { item: ProjectPaperWithSummary }) => {
    const paperId = item.paper.short_id || getShortId(item.paper.id);
    const baseUrl = getSimplePaperUrl(paperId, item.paper.title);
    const linkHref = digestId
      ? attachStandalonePaperBackLink(baseUrl, {
          backHref: summariesPath,
          backText: `← Back to ${projectName}`,
        })
      : baseUrl;

    return (
      <View style={styles.itemWrapper}>
        <PaperCard
          paper={item.paper}
          linkHref={linkHref}
          showRank={false}
          showScores={false}
          eli5={item.eli5}
        />
      </View>
    );
  }, [digestId, projectName, summariesPath]);

  const scrollToTop = useCallback(() => {
    flatListRef.current?.scrollToOffset({ offset: 0, animated: true });
  }, []);

  const ListHeader = (
    <View style={styles.contentWrapper}>
      {/* Back link */}
      <Link href={projectPath} asChild>
        <TouchableOpacity style={styles.backLink}>
          <Text style={[styles.backLinkText, fontsLoaded && interSemiBold]}><Trans id="digests.byDigestid.summaries.backTo" comment="UI copy in app/(app)/digests/[digestId]/summaries.tsx">
            <FontAwesome6 name="arrow-left" size={14} /> Back to {projectName}
          </Trans></Text>
        </TouchableOpacity>
      </Link>

      {/* Header */}
      <View style={styles.headerSection}>
        <Text
          style={[
            fonts.displayLarge,
            {
              fontSize: 36,
              fontWeight: '600',
              lineHeight: 44,
              color: colors.text900,
              marginBottom: 16
            },
            fontsLoaded && interSemiBold
          ]}
        ><Trans id="digests.byDigestid.summaries.paperSummaries" comment="UI copy in app/(app)/digests/[digestId]/summaries.tsx">
          Paper Summaries
        </Trans></Text>
        <Text
          style={[
            fonts.bodyLarge,
            {
              color: colors.text700,
              marginBottom: 32,
              lineHeight: 28
            },
            fontsLoaded && interRegular
          ]}
        ><Trans id="digests.byDigestid.summaries.aiGeneratedSummariesForPapersIn" comment="UI copy in app/(app)/digests/[digestId]/summaries.tsx">
          AI-generated summaries for papers in {projectName}.
        </Trans></Text>
      </View>

      {previewAccess === 'checking' && (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="small" color={colors.primaryGreen} />
          <Text style={[fonts.bodyMedium, { color: colors.text500, marginTop: 12 }, fontsLoaded && interRegular]}><Trans id="digests.byDigestid.summaries.checkingProjectAccess" comment="UI copy in app/(app)/digests/[digestId]/summaries.tsx">
            Checking project access...
          </Trans></Text>
        </View>
      )}

      {previewAccessRetryMessage && (
        <Banner
          type="error"
          title={msg({ id: "digests.byDigestid.summaries.couldNotVerifyProjectAccess", message: "Could not verify project access", comment: "UI title in app/(app)/digests/[digestId]/summaries.tsx" })}
          message={previewAccessRetryMessage}
          action={{
            label: msg({ id: 'common.retry', message: 'Retry' }),
            onPress: () => { void refreshPreviewAccess(); },
          }}
        />
      )}

      {previewAccess === 'restricted' && (
        <Banner
          type="info"
          title={msg({ id: "digests.byDigestid.summaries.aiSummariesAreNotPartOfTheOneTimePreview", message: "AI summaries are not part of the one-time preview", comment: "UI title in app/(app)/digests/[digestId]/summaries.tsx" })}
          message={msg({ id: "digests.byDigestid.summaries.startAFreeTrialToResumeTrackingAndCreateAiSummariesForThesePapers", message: "Start a free trial to resume tracking and create AI summaries for these papers.", comment: "UI message in app/(app)/digests/[digestId]/summaries.tsx" })}
          action={{
            label: msg({ id: 'common.startFreeTrial', message: 'Start free trial' }),
            onPress: () => router.push(previewTrialHref),
          }}
        />
      )}

      {/* Search filter */}
      {previewAccess === 'unrestricted' && !isLoading && !error && allPapers.length > 0 && (
        <SearchInput
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholder={i18n._(msg({ id: "digests.byDigestid.summaries.searchByTitleAuthorsOrSummary", message: "Search by title, authors, or summary...", comment: "UI placeholder in app/(app)/digests/[digestId]/summaries.tsx" }))}
        />
      )}

      {/* Loading state */}
      {previewAccess === 'unrestricted' && isLoading && (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={colors.primaryGreen} />
          <Text style={[
            fonts.bodyMedium,
            { color: colors.text500, marginTop: 16 },
            fontsLoaded && interRegular
          ]}><Trans id="digests.byDigestid.summaries.loadingSummaries" comment="UI copy in app/(app)/digests/[digestId]/summaries.tsx">
            Loading summaries...
          </Trans></Text>
        </View>
      )}

      {/* Error state */}
      {previewAccess === 'unrestricted' && error && !isLoading && (
        <Banner type="error" title={msg({ id: "digests.byDigestid.summaries.couldnTLoadSummaries", message: "Couldn't load summaries", comment: "UI title in app/(app)/digests/[digestId]/summaries.tsx" })} message={msg({ id: "digests.byDigestid.summaries.pleaseTryAgainLater", message: "Please try again later.", comment: "UI message in app/(app)/digests/[digestId]/summaries.tsx" })} />
      )}

      {/* Empty state */}
      {previewAccess === 'unrestricted' && !isLoading && !error && allPapers.length === 0 && (
        <View style={styles.centerContainer}>
          <FontAwesome6 name="file-lines" size={48} color={colors.text500} />
          <Text style={[
            fonts.bodyLarge,
            { color: colors.text700, marginBottom: 8, marginTop: 16 },
            fontsLoaded && interSemiBold
          ]}><Trans id="digests.byDigestid.summaries.noSummariesYet" comment="UI copy in app/(app)/digests/[digestId]/summaries.tsx">
            No summaries yet
          </Trans></Text>
          <Text style={[
            fonts.bodyMedium,
            { color: colors.text500, textAlign: 'center', maxWidth: 400 },
            fontsLoaded && interRegular
          ]}><Trans id="digests.byDigestid.summaries.generateSummariesForPapersInThisDigestToSeeThem" comment="UI copy in app/(app)/digests/[digestId]/summaries.tsx">
            Generate summaries for papers in this digest to see them here.
          </Trans></Text>
        </View>
      )}

      {/* No search results */}
      {previewAccess === 'unrestricted' && !isLoading && !error && allPapers.length > 0 && filteredPapers.length === 0 && searchQuery.trim() && (
        <View style={styles.centerContainer}>
          <Text style={[
            fonts.bodyLarge,
            { color: colors.text700, marginBottom: 8 },
            fontsLoaded && interSemiBold
          ]}><Trans id="digests.byDigestid.summaries.noResultsFound" comment="UI copy in app/(app)/digests/[digestId]/summaries.tsx">
            No results found
          </Trans></Text>
          <Text style={[
            fonts.bodyMedium,
            { color: colors.text500 },
            fontsLoaded && interRegular
          ]}><Trans id="digests.byDigestid.summaries.tryADifferentSearchTerm" comment="UI copy in app/(app)/digests/[digestId]/summaries.tsx">
            Try a different search term
          </Trans></Text>
        </View>
      )}
    </View>
  );

  const ListFooterComponent = (
    <View>
      {previewAccess === 'unrestricted' && isFetchingNextPage && (
        <View style={styles.contentWrapper}>
          <ActivityIndicator size="small" color={colors.primaryGreen} style={{ paddingVertical: 24 }} />
        </View>
      )}
      {visiblePapers.length > 0 && (
        <View style={styles.contentWrapper}>
          <TouchableOpacity
            style={styles.backToTop}
            onPress={scrollToTop}
            activeOpacity={0.7}
          >
            <FontAwesome6 name="arrow-up" size={12} color={colors.primaryGreen} />
            <Text style={[styles.backToTopText, fontsLoaded && interSemiBold]}><Trans id="digests.byDigestid.summaries.backToTop" comment="UI copy in app/(app)/digests/[digestId]/summaries.tsx">Back to top</Trans></Text>
          </TouchableOpacity>
        </View>
      )}
      <Footer />
    </View>
  );

  return (
    <>
      <MetaTags
        title={`${projectName} - Summaries - Paperzilla`}
        description={`Browse papers with AI-generated summaries from ${projectName}.`}
        url={`https://paperzilla.ai${buildCanonicalDashboardPath(`/${digestId}/summaries`)}`}
      />
      <Stack.Screen options={{ headerShown: false }} />
      <View style={styles.page}>
        <Header hidePreviewTrialBanner={previewAccess === 'restricted'} />
        <FlatList
          ref={flatListRef}
          data={visiblePapers}
          renderItem={renderItem}
          keyExtractor={item => item.paper.id}
          ListHeaderComponent={ListHeader}
          ListFooterComponent={ListFooterComponent}
          contentContainerStyle={styles.listContent}
          ItemSeparatorComponent={() => <View style={{ height: 16 }} />}
          onEndReached={handleEndReached}
          onEndReachedThreshold={0.5}
          showsVerticalScrollIndicator={false}
        />
      </View>
    </>
  );
}

export default withProjectProvider(ProjectSummariesPage);

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: colors.bgWeak,
  },
  listContent: {
    flexGrow: 1,
  },
  contentWrapper: {
    width: '100%',
    maxWidth: 900,
    alignSelf: 'center',
    paddingHorizontal: 24,
  },
  itemWrapper: {
    width: '100%',
    maxWidth: 900,
    alignSelf: 'center',
    paddingHorizontal: 24,
  },
  backLink: {
    marginTop: 24,
    marginBottom: 16,
    alignSelf: 'flex-start',
  },
  backLinkText: {
    fontSize: 16,
    color: colors.primaryGreen,
    fontWeight: '600',
  },
  headerSection: {
    paddingTop: 16,
    paddingBottom: 4,
  },
  centerContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 80,
  },
  backToTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    marginTop: 32,
    marginBottom: 48,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.bgWeak,
  },
  backToTopText: {
    fontSize: 14,
    color: colors.primaryGreen,
    fontWeight: '600',
  },
});
